/**
 * Live inbox updates over `/svc/crm/ws` (token-authenticated WebSocket).
 *
 * Events are tiny id-only payloads published by the backend hub
 * (Postgres LISTEN/NOTIFY across workers); on receipt we invalidate the
 * relevant React Query caches so every pane refreshes without polling.
 * Also raises desktop notifications for new customer messages / AI drafts
 * when the tab is hidden.
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getAccessToken } from '@/api/client';

export interface CrmRealtimeEvent {
  type: string;
  conversation_id?: number | null;
  message_id?: number;
  direction?: 'in' | 'out';
  intent?: string;
  user_id?: number | null;
  order_id?: number | null;
}

const RETRY_BASE_MS = 2_000;
const RETRY_MAX_MS = 30_000;

export function useConversationsRealtime(options?: {
  onEvent?: (event: CrmRealtimeEvent) => void;
  notifications?: boolean;
}) {
  const qc = useQueryClient();
  const [connected, setConnected] = useState(false);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    let ws: WebSocket | null = null;
    let closed = false;
    let retryMs = RETRY_BASE_MS;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    if (optionsRef.current?.notifications && 'Notification' in window && Notification.permission === 'default') {
      void Notification.requestPermission();
    }

    function notify(title: string, body: string) {
      if (!optionsRef.current?.notifications) return;
      if (document.visibilityState === 'visible') return;
      if (!('Notification' in window) || Notification.permission !== 'granted') return;
      try {
        new Notification(title, { body, icon: '/favicon.ico', tag: 'crm-inbox' });
      } catch {
        /* Notification constructor unsupported (e.g. Android Chrome) */
      }
    }

    function handleEvent(event: CrmRealtimeEvent) {
      if (event.type === 'ping') return;
      const convId = event.conversation_id;
      // Refresh the list on any activity; refresh the open thread + context.
      void qc.invalidateQueries({ queryKey: ['bi', 'crm', 'conversations'] });
      if (convId) {
        void qc.invalidateQueries({ queryKey: ['bi', 'crm', 'customer-context', convId] });
      }
      if (event.type === 'message.created' && event.direction === 'in') {
        notify('New customer message', `Conversation #${convId ?? ''}`);
      } else if (event.type === 'draft.created') {
        notify('AI draft awaiting review', `Conversation #${convId ?? ''} · ${event.intent ?? ''}`);
      }
      optionsRef.current?.onEvent?.(event);
    }

    function connect() {
      const token = getAccessToken();
      if (!token) {
        retryTimer = setTimeout(connect, retryMs);
        return;
      }
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      ws = new WebSocket(`${proto}://${window.location.host}/svc/crm/ws?token=${encodeURIComponent(token)}`);
      ws.onopen = () => {
        setConnected(true);
        retryMs = RETRY_BASE_MS;
      };
      ws.onmessage = (msg) => {
        try {
          handleEvent(JSON.parse(msg.data as string) as CrmRealtimeEvent);
        } catch {
          /* malformed frame — ignore */
        }
      };
      ws.onclose = () => {
        setConnected(false);
        if (closed) return;
        retryTimer = setTimeout(connect, retryMs);
        retryMs = Math.min(retryMs * 2, RETRY_MAX_MS);
      };
      ws.onerror = () => ws?.close();
    }

    connect();
    return () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      ws?.close();
    };
  }, [qc]);

  return { connected };
}
